import { computed, makeObservable } from 'mobx'

import { SaveLocationsDocument } from '~/client/graph/operations/generated/Locations.generated'
import MapViewLocationIcon from '~/client/src/shared/enums/SitemapAttributeIcon'
import Building from '~/client/src/shared/models/LocationObjects/Building'
import { creatableLocationKindsOf } from '~/client/src/shared/resources/domain/creatableLocationKinds'
import { LocationKind } from '~/client/src/shared/resources/domain/locationKind'
import ILocationAttributeDto from '~/client/src/shared/types/ILocationAttributeDto'

import Level from '../../models/LocationObjects/Level'
import LocationBase from '../../models/LocationObjects/LocationBase'
import EventsStore from '../EventStore/Events.store'
import GraphExecutorStore from './GraphExecutor.store'
import LocationAttributesStoreBase from './LocationAttributesBase.store'
import type ProjectResourcesStore from './ProjectResources.store'

export default class BuildingsStore extends LocationAttributesStoreBase<Building> {
  protected saveMutation = SaveLocationsDocument
  protected graphName = 'locations'
  protected defaultIconName = MapViewLocationIcon.Building

  public constructor(
    eventsStore: EventsStore,
    graphExecutorStore: GraphExecutorStore,
    private readonly locationResourcesStore: Pick<
      ProjectResourcesStore<unknown, unknown>,
      'propertiesStateFor'
    >,
  ) {
    super(eventsStore, graphExecutorStore)

    makeObservable(this)
  }

  // for sitemap LBS hierarhy purposes
  @computed
  public get firstBuilding(): Building {
    return this.list.sort((a, b) => a?.name?.localeCompare(b?.name))[0] || null
  }

  // should save at least 1 level to building
  public async additionalSaveAction(
    item: Building,
    oldItem: Building,
  ): Promise<void> {
    const { levels: allLevels } = this.eventsStore.appState
    const levels = Array.from(allLevels.values()).filter(
      l => !l.isDeleted && l.isParent(item),
    )
    if (oldItem?.id || levels?.length) {
      return
    }

    const { id: projectId } = this.eventsStore.appState.activeProject
    const { isReady, isCreatable } = creatableLocationKindsOf(
      this.locationResourcesStore.propertiesStateFor(projectId),
    )
    if (isReady && !isCreatable(LocationKind.Level)) {
      return
    }
    const name = `${item.levelBaseName} ${1}`
    const level = new Level(
      null,
      name,
      item.color,
      MapViewLocationIcon.Level,
      projectId,
      LocationBase.generateCodeFromName(name),
      [],
      { parentId: item.id },
    )

    level.isNameImplicit = true

    const { data } = await this.graphExecutorStore.mutate(
      SaveLocationsDocument,
      { locations: [level.getDto()] },
    )
    const savedLevel = data?.saveManyLocations?.[0]
    if (!savedLevel) {
      return
    }

    item.levelsOrder = [savedLevel.id]
    item.groundLevel = savedLevel.id

    await this.graphExecutorStore.mutate(SaveLocationsDocument, {
      locations: [item.getDto()],
    })
  }

  public get byId() {
    const { buildings } = this.eventsStore.appState
    return buildings
  }

  protected fromDto(dto: ILocationAttributeDto): Building {
    return Building.fromDto(dto)
  }
}
